<template>
  <div class="ag-cell-code-list-renderer">
    <CodePillList
      class="ag-cell-code-list-renderer__code-pill-list"
      data-testid="grid-code-pill-list"
      justify="left"
      :wrap-column="isColumnWrapped"
    >
      <template #pill>
        <CodePill
          v-for="attachment in codesToRender"
          :count="attachment.code.occurrence"
          :key="attachment.code.id"
          :item="attachment.code"
        />
      </template>
      <template #action>
        <ADropdown
          v-if="showTruncationButton"
          :shown="showAllCodesPopover"
          auto-hide
          @auto-hide="showAllCodesPopover = false"
          placement="top-end"
        >
          <CodePill
            class="ag-cell-code-list-renderer__showmore-pill"
            :title="$t('quotationOverview.showAllCodes')"
            :item="{}"
            type="icon"
            clickable
            data-test-id="show-all-codes-button"
            @click="togglePopover"
          >
            <template #icon>
              <MoreIcon16 />
            </template>
          </CodePill>
          <template #popper>
            <CodeList
              ref="CodeTruncationPopover"
              class="ag-cell-code-list-renderer__popover-content"
              data-test-id="code-list-popover"
              :items="allCodes"
            />
          </template>
        </ADropdown>
      </template>
    </CodePillList>
    <transition name="fade-transition">
      <span v-if="codesToRender.length > 0" class="ag-cell__gradient"></span>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CodeList from '@components/List/CodeList.vue';
import MoreIcon16 from '@components/Icons/MoreIcon16.vue';
import { ProjectCode, ProjectQuotation } from '@/types';
import { ICellRendererParams } from 'ag-grid-community';

/**
 * ag-grid cell renderer to show a list of codes
 *
 * Assumes the data of the ag-grid row is a Quotation, although the renderer is only concerned with an
 * array of codes
 */
export default defineComponent({
  name: 'CodeListRenderer',
  components: {
    MoreIcon16,
    CodeList,
  },

  props: {
    params: {
      type: Object as PropType<ICellRendererParams<ProjectQuotation, Array<ProjectCode>>>,
      required: true,
    },
  },
  data(): {
    showAllCodesPopover: boolean;
    isColumnWrapped: boolean;
    colId: string;
  } {
    return {
      showAllCodesPopover: false,
      isColumnWrapped: true,
      colId: '',
    };
  },
  created() {
    this.colId = this.params.column.getColId();
    this.$watch(
      () => this.$store.state.gridWrap.wrappedColumns[this.colId],
      () => {
        this.isColumnWrapped = this.$store.getters['gridWrap/isColumnWrapped'](this.colId);
      },
      { immediate: true }
    );
  },
  computed: {
    codesToRender(): Array<ProjectCode> {
      if (this.params.value.length <= 8) {
        return this.params.value;
      }

      return [...this.params.value].slice(0, 8);
    },
    showTruncationButton(): boolean {
      return this.params.value.length > 8;
    },
    allCodes(): Array<ProjectCode> {
      return this.params.value;
    },
  },

  methods: {
    togglePopover(event: MouseEvent) {
      // stop propagation to prevent the ag-grid onCellClicked event from firing
      // and the QuotationUpsert modal from opening
      event.stopPropagation();

      this.showAllCodesPopover = !this.showAllCodesPopover;
    },

    preventScroll(event: WheelEvent) {
      event.preventDefault();
      event.stopPropagation();
    },
  },

  beforeUnmount() {
    document.querySelector('.ag-body-viewport')?.removeEventListener('wheel', this.preventScroll);
  },

  watch: {
    /**
     * Prevent scrolling of grid when the popover with all codes is shown, because
     * the popover flickers when you scroll
     */
    showAllCodesPopover() {
      const grid = document.querySelector('.ag-body-viewport');
      if (this.showAllCodesPopover === true) {
        return grid.addEventListener('wheel', this.preventScroll, { passive: false });
      }

      return grid.removeEventListener('wheel', this.preventScroll);
    },
  },
});
</script>

<style lang="scss">
@use 'settings/settings';
@use 'mixins/base-renderer';
@use 'mixins/cell-gradient';

@include cell-gradient.cell-gradient;

.ag-cell-code-list-renderer {
  @include base-renderer.base-renderer;
  padding: 16px 0;
  height: 100%;
  position: relative;

  &__code-pill-list {
    width: 200px;
    min-height: 32px;
    height: 100%;
    cursor: pointer;
  }

  &__showmore-pill .code-pill__link {
    &:focus {
      box-shadow: settings.$box-shadow-border-grey-50;
    }
  }

  &__popover-content {
    box-shadow: settings.$box-shadow-layer-2;
  }
}
</style>
